import {
  ANALYTICS_CONSTANTS,
  AnalyticsActions,
  AnalyticsProperties,
  DatePickerSelectionProps,
  NoteCopyProps,
  NoteSentenceProps,
} from '@ax/constants';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useDeviceId from '@/hooks/useDeviceId.tsx';
import { selectProviderUserInfo, selectMixpanelToken, useAppSelector } from '@ax/redux-store';
import { AnalyticsService } from '@ax/service';

const useAnalytics = (): AnalyticsActions => {
  const providerUserInfo = useAppSelector(selectProviderUserInfo);
  const pendingEvents = useRef<{ name: string; props?: AnalyticsProperties }[]>([]);
  const { deviceId } = useDeviceId();
  const mixpanelToken = useAppSelector(selectMixpanelToken);
  const analyticsService = useMemo(
    () =>
      new AnalyticsService(
        mixpanelToken ?? process.env.REACT_APP_MIXPANEL_TOKEN ?? '',
        providerUserInfo,
        deviceId ?? '',
      ),
    [mixpanelToken, providerUserInfo, deviceId],
  );

  const registerProps = useCallback(
    (properties: AnalyticsProperties) => {
      return analyticsService.processProperties(properties);
    },
    [analyticsService],
  );

  /**
   * This effect will run when the provider settings are loaded and will send all the pending events.
   */
  useEffect(() => {
    if (providerUserInfo) {
      pendingEvents.current.forEach((event) => {
        const props = registerProps(event.props ?? {});
        analyticsService.trackEvent(event.name, props);
      });
      pendingEvents.current = [];
    }
  }, [providerUserInfo, analyticsService, registerProps]);

  const onVerbalizationEducationModal = (properties: AnalyticsProperties) => {
    analyticsService.onVerbalizationEducationModal(properties);
  };

  const onVisitAddition = (properties: AnalyticsProperties) => {
    analyticsService.onVisitAddition(properties);
  };

  const onVisitDeletion = (properties: AnalyticsProperties) => {
    analyticsService.onVisitDeletion(properties);
  };

  const onVisitArchived = (properties: AnalyticsProperties) => {
    analyticsService.onVisitArchived(properties);
  };

  const onVisitSelection = (properties: AnalyticsProperties) => {
    analyticsService.onVisitSelection(properties);
  };

  const onDatePickerSelection = (properties: DatePickerSelectionProps) => {
    analyticsService.onDatePickerSelection(properties);
  };

  const onRecordingStart = (properties: AnalyticsProperties) => {
    analyticsService.onRecordingStart(properties);
  };

  const onRecordingStop = (properties: AnalyticsProperties) => {
    analyticsService.onRecordingStop(properties);
  };

  const onNoteCopied = (properties: NoteCopyProps) => {
    analyticsService.onNoteCopied(properties);
  };

  const onNoteEdit = (properties: NoteSentenceProps) => {
    analyticsService.onNoteEdit(properties);
  };

  const onNoteUploaded = (properties: AnalyticsProperties) => {
    analyticsService.onNoteUploaded(properties);
  };

  const onNoteUploadFailed = (properties: AnalyticsProperties) => {
    analyticsService.onNoteUploadFailed(properties);
  };

  const onUserLogout = (properties?: AnalyticsProperties) => {
    analyticsService.onUserLogout(properties);
  };

  const onUserLogin = (properties?: AnalyticsProperties) => {
    /**
     * If the provider settings are not loaded yet, we will store the event in a ref until the settings are loaded.
     * See the effect above.
     */
    if (!providerUserInfo) {
      pendingEvents.current.push({ name: ANALYTICS_CONSTANTS.event.logIn, props: properties });
      return;
    }

    analyticsService.onUserLogin(properties);
  };

  const onUserLoginFailedPWD = (properties?: AnalyticsProperties) => {
    analyticsService.onUserLoginFailedPWD(properties);
  };

  const onUsernameInput = (properties: AnalyticsProperties) => {
    analyticsService.onUsernameInput(properties);
  };

  const onSSORedirect = (properties: AnalyticsProperties) => {
    analyticsService.onSSORedirect(properties);
  };

  const onSSOReturn = (properties: AnalyticsProperties) => {
    analyticsService.onSSOReturn(properties);
  };

  const onCreatePin = (properties: AnalyticsProperties) => {
    analyticsService.onCreatePin(properties);
  };

  const onVerifyPin = (properties: AnalyticsProperties) => {
    analyticsService.onVerifyPin(properties);
  };

  const onTapNotYou = (properties: AnalyticsProperties) => {
    analyticsService.onTapNotYou(properties);
  };

  const onEnteredValidPin = (properties: AnalyticsProperties) => {
    const props = registerProps({
      ...properties,
      method: ANALYTICS_CONSTANTS.loginMethods.passcode,
    });

    /**
     * If the provider settings are not loaded yet, we will store the event in a ref until the settings are loaded.
     * See the effect above.
     */
    if (!providerUserInfo) {
      pendingEvents.current.push({ name: ANALYTICS_CONSTANTS.event.enteredValidPin, props });
      return;
    }

    analyticsService.onEnteredValidPin(properties);
  };

  const onEnteredInvalidPin = (properties: AnalyticsProperties) => {
    analyticsService.onEnteredInvalidPin(properties);
  };

  const onAddNewTemplate = (properties: AnalyticsProperties) => {
    analyticsService.onAddNewTemplate(properties);
  };

  const onSaveTemplate = (properties: AnalyticsProperties) => {
    analyticsService.onSaveTemplate(properties);
  };

  const onUpdateTemplate = (properties: AnalyticsProperties) => {
    analyticsService.onUpdateTemplate(properties);
  };

  const onDeleteTemplate = (properties: AnalyticsProperties) => {
    analyticsService.onDeleteTemplate(properties);
  };

  const onDeleteTemplateConfirm = (properties: AnalyticsProperties) => {
    analyticsService.onDeleteTemplateConfirm(properties);
  };

  const onClearTemplate = (properties: AnalyticsProperties) => {
    analyticsService.onClearTemplate(properties);
  };

  const onClearTemplateConfirm = (properties: AnalyticsProperties) => {
    analyticsService.onClearTemplateConfirm(properties);
  };

  const onTapResetPIN = (properties?: AnalyticsProperties) => {
    analyticsService.onTapResetPIN(properties);
  };

  const onPreferencesViewed = () => {
    analyticsService.onPreferencesViewed({});
  };

  const onPreferencesSaved = (properties: AnalyticsProperties) => {
    analyticsService.onPreferencesSaved(properties);
  };

  const onInfoModalOpened = (properties: AnalyticsProperties) => {
    analyticsService.onInfoModalOpened(properties);
  };

  const onInfoModalClosed = (properties: AnalyticsProperties) => {
    analyticsService.onInfoModalClosed(properties);
  };

  const onResetPIN = (properties?: AnalyticsProperties) => {
    /**
     * If the provider settings are not loaded yet, we will store the event in a ref until the settings are loaded.
     * See the effect above.
     */
    if (!providerUserInfo) {
      pendingEvents.current.push({ name: ANALYTICS_CONSTANTS.event.resetPIN, props: properties });
      return;
    }

    analyticsService.onResetPIN(properties);
  };

  const onCareCuesAddToNoteButtonTapped = (properties: { noteId: string }) => {
    analyticsService.onCareCuesAddToNoteButtonTapped(properties);
  };

  const onCareCuesArchived = (properties: { noteId: string }) => {
    analyticsService.onCareCuesArchived(properties);
  };

  const onCareCuesSeen = (properties: AnalyticsProperties) => {
    analyticsService.onCareCuesSeen(properties);
  };

  const onNavigatedToCareCuesFromCareCuesUploadModal = (properties: { noteId: string }) => {
    analyticsService.onNavigatedToCareCuesFromCareCuesUploadModal(properties);
  };

  const onUploadedNoteFromCareCuesUploadModal = (properties: { noteId: string }) => {
    analyticsService.onUploadedNoteFromCareCuesUploadModal(properties);
  };

  const onUploadCareCuesModalTriggered = (properties: { noteId: string }) => {
    analyticsService.onUploadCareCuesModalTriggered(properties);
  };

  const onNoteLevelThumbsUp = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelThumbsUp(properties);
  };

  const onNoteLevelThumbsDown = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelThumbsDown(properties);
  };

  const onNoteLevelFeedbackReason = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelFeedbackReason(properties);
  };

  const onNoteLevelFeedbackTextSubmitted = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelFeedbackTextSubmitted(properties);
  };

  const onNoteLevelFeedbackModalOpened = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelFeedbackModalOpened(properties);
  };

  const onNoteLevelFeedbackModalClosed = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelFeedbackModalClosed(properties);
  };

  const onNoteLevelFeedbackReasonAdded = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelFeedbackReasonAdded(properties);
  };

  const onNoteLevelFeedbackReasonRemoved = (properties: AnalyticsProperties) => {
    analyticsService.onNoteLevelFeedbackReasonRemoved(properties);
  };

  const onLabInterpretationsViewed = (properties: AnalyticsProperties) => {
    analyticsService.onLabInterpretationsViewed(properties);
  };

  const onInterpretationRejected = (properties: AnalyticsProperties) => {
    analyticsService.onInterpretationRejected(properties);
  };

  const onInterpretationAccepted = (properties: AnalyticsProperties) => {
    analyticsService.onInterpretationAccepted(properties);
  };

  const onInterpretationEdited = (properties: AnalyticsProperties) => {
    analyticsService.onInterpretationEdited(properties);
  };

  return {
    onVisitAddition,
    onVisitDeletion,
    onVisitSelection,
    onDatePickerSelection,
    onRecordingStart,
    onRecordingStop,
    onNoteCopied,
    onNoteEdit,
    onUserLogout,
    onVisitArchived,
    onNoteUploaded,
    onNoteUploadFailed,
    onUserLogin,
    onUserLoginFailedPWD,
    onUsernameInput,
    onSSORedirect,
    onSSOReturn,
    onCreatePin,
    onVerifyPin,
    onTapNotYou,
    onEnteredValidPin,
    onEnteredInvalidPin,
    onAddNewTemplate,
    onSaveTemplate,
    onDeleteTemplate,
    onDeleteTemplateConfirm,
    onClearTemplate,
    onUpdateTemplate,
    onClearTemplateConfirm,
    onTapResetPIN,
    onResetPIN,
    onPreferencesViewed,
    onPreferencesSaved,
    onInfoModalOpened,
    onInfoModalClosed,
    onCareCuesAddToNoteButtonTapped,
    onCareCuesArchived,
    onCareCuesSeen,
    onNavigatedToCareCuesFromCareCuesUploadModal: onNavigatedToCareCuesFromCareCuesUploadModal,
    onUploadedNoteFromCareCuesUploadModal: onUploadedNoteFromCareCuesUploadModal,
    onUploadCareCuesModalTriggered: onUploadCareCuesModalTriggered,
    onVerbalizationEducationModal,
    onNoteLevelThumbsUp,
    onNoteLevelThumbsDown,
    onNoteLevelFeedbackReason,
    onNoteLevelFeedbackTextSubmitted,
    onNoteLevelFeedbackModalOpened,
    onNoteLevelFeedbackModalClosed,
    onNoteLevelFeedbackReasonAdded,
    onNoteLevelFeedbackReasonRemoved,
    onLabInterpretationsViewed,
    onInterpretationRejected,
    onInterpretationAccepted,
    onInterpretationEdited,
  };
};

export default useAnalytics;
